import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useMatch, useNavigate, useResolvedPath } from 'react-router-dom';

import { ProductReadModel } from '@client/api';
import { isDefinedString, isNotNullish, TableCell, TableRow, Text } from '@client/toolkit';

import { ProductsPaths } from '../../products.router';

interface ProductListRowProps {
  product?: ProductReadModel;
  isFetching?: boolean;
}

export function ProductListRow({ product, isFetching }: ProductListRowProps) {
  const { t: tGlobal } = useTranslation('global');
  const productPath = useResolvedPath(ProductsPaths.Product);
  const productPathMatch = useMatch<'id', string>({ path: productPath.pathname });
  const selectedProductId = useMemo(
    () => (isNotNullish(productPathMatch) ? productPathMatch.params.id : null),
    [productPathMatch],
  );

  const navigate = useNavigate();

  const { t: tType } = useTranslation('type');

  const handleClick = useCallback(() => {
    if (product?.id == null) return;

    if (product.id === selectedProductId) {
      return navigate(generatePath(ProductsPaths.Product));
    }

    navigate(generatePath(ProductsPaths.Product, { id: product.id }));
  }, [navigate, product?.id, selectedProductId]);

  return (
    <TableRow hover selected={selectedProductId === product?.id} onClick={handleClick}>
      <TableCell className="font-semibold">
        <Text isEmpty={!isDefinedString(product?.name)} loading={isFetching}>
          {product?.name}
        </Text>
      </TableCell>
      <TableCell>
        <Text isEmpty={!isDefinedString(product?.category)} loading={isFetching}>
          {tType(`ProductCategory.${product?.category ?? 'Other'}`)}
        </Text>
      </TableCell>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(product?.unit?.scheme)}
          emptyValue={tGlobal('product.withoutUnitScheme')}
          loading={isFetching}
        >
          {tType(`UnitScheme.${product?.unit?.scheme}`)}
        </Text>

        {!isFetching && (
          <Text
            className="italic ml-1 text-secondary font-normal"
            isEmpty={!isDefinedString(product?.unit?.label)}
            emptyValue={''}
          >
            ({product?.unit?.label})
          </Text>
        )}
      </TableCell>
      <TableCell align="right">
        <Text isEmpty={!isDefinedString(product?.description)} loading={isFetching}>
          {product?.description}
        </Text>
      </TableCell>
    </TableRow>
  );
}
